import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import Navigation from "@/components/navigation";
import StarfieldBackground from "@/components/starfield-background";
import { Filter, Loader2, AlertCircle, Maximize2, Minimize2 } from "lucide-react";
import { apiService, ScheduleBlockType, } from "@/lib/api";
const START_HOUR = 10;
const END_HOUR = 24;
const formatHour = (h) => {
    const adj = h % 24;
    if (adj === 0)
        return "12 AM";
    if (adj < 12)
        return `${adj} AM`;
    if (adj === 12)
        return "12 PM";
    return `${adj - 12} PM`;
};
export default function ScheduleByVolunteer() {
    const [blocks, setBlocks] = useState([]);
    const [poolViews, setPoolViews] = useState([]);
    const [volunteerEntries, setVolunteerEntries] = useState([]);
    const [txsUsers, setTxsUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedDate, setSelectedDate] = useState("");
    const [selectedBlockType, setSelectedBlockType] = useState(ScheduleBlockType.Game);
    const [fullscreen, setFullscreen] = useState(false);
    const [currentMinute, setCurrentMinute] = useState(() => {
        const now = new Date();
        return now.getHours() * 60 + now.getMinutes();
    });
    useEffect(() => {
        setLoading(true);
        Promise.all([
            apiService.getScheduleBlocks(),
            apiService.getAllPoolViews(),
            apiService.getAllVolunteerEntries(),
            apiService.getTxsUsers(),
        ])
            .then(([blockData, views, volEntries, users]) => {
            setBlocks(blockData);
            setPoolViews(views);
            setVolunteerEntries(volEntries);
            setTxsUsers(users);
            const gameDates = blockData
                .filter(b => (b.blockTypeId ?? ScheduleBlockType.Game) === ScheduleBlockType.Game)
                .map(b => b.startTime.slice(0, 10))
                .sort();
            if (gameDates[0])
                setSelectedDate(gameDates[0]);
        })
            .catch(() => setError("Failed to load schedule. Please try again."))
            .finally(() => setLoading(false));
    }, []);
    useEffect(() => {
        const tick = () => {
            const now = new Date();
            setCurrentMinute(now.getHours() * 60 + now.getMinutes());
        };
        const id = setInterval(tick, 60000);
        return () => clearInterval(id);
    }, []);
    const isGameMode = selectedBlockType === ScheduleBlockType.Game;
    const availableDates = Array.from(new Set(blocks
        .filter(b => (b.blockTypeId ?? ScheduleBlockType.Game) === selectedBlockType)
        .map(b => b.startTime.slice(0, 10)))).sort();
    // If the current selected date has no blocks for this type, fall back to the first available
    const effectiveDate = availableDates.includes(selectedDate) ? selectedDate : (availableDates[0] ?? "");
    const filteredBlocks = blocks.filter(b => b.startTime.startsWith(effectiveDate) &&
        (b.blockTypeId ?? ScheduleBlockType.Game) === selectedBlockType);
    const blockById = new Map(filteredBlocks.map(b => [b.scheduleBlockId, b]));
    // Game mode: use pool views
    const filteredViews = poolViews.filter(v => v.scheduleBlockId != null && blockById.has(v.scheduleBlockId));
    const gameVolunteerMap = new Map();
    for (const v of filteredViews) {
        const key = v.gamertag ?? String(v.txsUserId);
        if (key && !gameVolunteerMap.has(key))
            gameVolunteerMap.set(key, v.gamertag ?? key);
    }
    // Non-game mode: use volunteer entries joined to users
    const filteredVolEntries = volunteerEntries.filter(e => e.scheduleBlockId != null && blockById.has(e.scheduleBlockId));
    const userMap = new Map(txsUsers.map(u => [u.txsUserId, u]));
    const nonGameVolunteerMap = new Map();
    for (const e of filteredVolEntries) {
        if (e.txsUserId != null && !nonGameVolunteerMap.has(e.txsUserId)) {
            const user = userMap.get(e.txsUserId);
            nonGameVolunteerMap.set(e.txsUserId, user?.gamerTag ?? user?.discordHandle ?? `User ${e.txsUserId}`);
        }
    }
    const volunteers = isGameMode
        ? Array.from(gameVolunteerMap.entries())
            .map(([k, v]) => ({ key: k, label: v }))
            .sort((a, b) => a.label.localeCompare(b.label))
        : Array.from(nonGameVolunteerMap.entries())
            .map(([k, v]) => ({ key: k, label: v }))
            .sort((a, b) => a.label.localeCompare(b.label));
    const getRelativeHour = (timeStr) => {
        const base = new Date(effectiveDate + 'T00:00:00');
        const time = new Date(timeStr);
        return Math.round((time.getTime() - base.getTime()) / (1000 * 60 * 60));
    };
    const timeSlots = Array.from({ length: END_HOUR - START_HOUR }, (_, i) => i + START_HOUR);
    const buildGameRow = (gamertag) => {
        const skipIndices = new Set();
        return timeSlots.map((hour, i) => {
            if (skipIndices.has(i))
                return { kind: 'skip' };
            const view = filteredViews.find(v => {
                if ((v.gamertag ?? String(v.txsUserId)) !== gamertag)
                    return false;
                const block = blockById.get(v.scheduleBlockId);
                return block != null && getRelativeHour(block.startTime) === hour;
            });
            if (view) {
                const block = blockById.get(view.scheduleBlockId);
                const colSpan = Math.max(1, getRelativeHour(block.endTime) - hour);
                for (let j = i + 1; j < i + colSpan; j++)
                    skipIndices.add(j);
                return { kind: 'block', block, colSpan };
            }
            return { kind: 'empty' };
        });
    };
    const buildVolunteerRow = (userId) => {
        const skipIndices = new Set();
        return timeSlots.map((hour, i) => {
            if (skipIndices.has(i))
                return { kind: 'skip' };
            const entry = filteredVolEntries.find(e => {
                if (e.txsUserId !== userId)
                    return false;
                const block = blockById.get(e.scheduleBlockId);
                return block != null && getRelativeHour(block.startTime) === hour;
            });
            if (entry) {
                const block = blockById.get(entry.scheduleBlockId);
                const colSpan = Math.max(1, getRelativeHour(block.endTime) - hour);
                for (let j = i + 1; j < i + colSpan; j++)
                    skipIndices.add(j);
                return { kind: 'block', block, colSpan };
            }
            return { kind: 'empty' };
        });
    };
    const blockCellColor = selectedBlockType === ScheduleBlockType.Registration ? '#1D4ED8CC' :
        selectedBlockType === ScheduleBlockType.Merchandise ? '#B45309CC' :
            '#15803DCC';
    const currentHour = Math.floor(currentMinute / 60);
    const isCurrentHour = (hour) => hour === currentHour;
    const handleBlockTypeChange = (type) => {
        setSelectedBlockType(type);
        const dates = Array.from(new Set(blocks
            .filter(b => (b.blockTypeId ?? ScheduleBlockType.Game) === type)
            .map(b => b.startTime.slice(0, 10)))).sort();
        if (dates[0])
            setSelectedDate(dates[0]);
    };
    const dateSelector = (_jsxs("div", { className: "flex items-center gap-2 flex-wrap", children: [_jsx(Filter, { className: "w-4 h-4 text-gray-400" }), _jsxs("select", { value: selectedBlockType, onChange: e => handleBlockTypeChange(parseInt(e.target.value)), disabled: loading, className: "px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-txs-red text-sm disabled:opacity-50", children: [_jsx("option", { value: ScheduleBlockType.Game, className: "bg-space-dark", children: "Game" }), _jsx("option", { value: ScheduleBlockType.Registration, className: "bg-space-dark", children: "Registration" }), _jsx("option", { value: ScheduleBlockType.Merchandise, className: "bg-space-dark", children: "Merchandise" })] }), _jsx("select", { value: effectiveDate, onChange: e => setSelectedDate(e.target.value), disabled: loading || availableDates.length === 0, className: "px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-txs-red text-sm disabled:opacity-50", children: availableDates.map(d => (_jsx("option", { value: d, className: "bg-space-dark", children: new Date(d + 'T12:00:00').toLocaleDateString('en-US', {
                        weekday: 'long', month: 'long', day: 'numeric'
                    }) }, d))) }), _jsx("button", { onClick: () => setFullscreen(f => !f), title: fullscreen ? "Exit fullscreen" : "Fullscreen view", className: "p-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-gray-400 hover:text-white transition-colors", children: fullscreen ? _jsx(Minimize2, { className: "w-4 h-4" }) : _jsx(Maximize2, { className: "w-4 h-4" }) })] }));
    const scheduleTable = (_jsxs(_Fragment, { children: [loading && (_jsxs("div", { className: "flex items-center justify-center gap-3 py-24 text-gray-400", children: [_jsx(Loader2, { className: "w-6 h-6 animate-spin" }), _jsx("span", { className: "font-orbitron text-sm", children: "Loading schedule..." })] })), error && (_jsxs("div", { className: "flex items-center gap-3 p-6 rounded-xl border border-red-500/30 bg-red-500/10 text-red-400", children: [_jsx(AlertCircle, { className: "w-5 h-5 flex-shrink-0" }), _jsx("span", { children: error })] })), !loading && !error && volunteers.length === 0 && (_jsx("div", { className: "text-center py-24 text-gray-500", children: _jsx("p", { className: "font-orbitron text-sm", children: "No volunteers found for this date." }) })), !loading && !error && volunteers.length > 0 && (_jsx("div", { className: fullscreen ? "flex-1 overflow-auto" : "overflow-auto rounded-xl border border-white/10", children: _jsxs("table", { className: "border-collapse text-sm", style: { tableLayout: 'fixed', width: '100%' }, children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { className: "border border-gray-600 p-3 bg-gray-800 font-orbitron font-bold text-center sticky left-0 top-0 z-30 text-xs", style: { minWidth: 140 }, children: effectiveDate
                                            ? new Date(effectiveDate + 'T12:00:00').toLocaleDateString('en-US', {
                                                weekday: 'long', month: 'numeric', day: 'numeric'
                                            }).toUpperCase()
                                            : '' }), timeSlots.map(hour => (_jsx("th", { className: "border border-gray-600 p-2 font-orbitron font-bold text-center sticky top-0 z-20 text-xs", style: {
                                            minWidth: 70,
                                            backgroundColor: isCurrentHour(hour) ? '#EF444466' : '#1F2937',
                                            outline: isCurrentHour(hour) ? '2px solid #EF4444' : undefined,
                                        }, children: formatHour(hour) }, hour)))] }) }), _jsx("tbody", { children: volunteers.map(({ key, label }) => {
                                const row = isGameMode
                                    ? buildGameRow(String(key))
                                    : buildVolunteerRow(key);
                                return (_jsxs("tr", { children: [_jsx("td", { className: "border border-gray-700 px-3 py-2 font-semibold text-white bg-gray-900 sticky left-0 z-10 text-sm", style: { minWidth: 140 }, children: label }), row.map((cell, i) => {
                                            if (cell.kind === 'skip')
                                                return null;
                                            const hour = timeSlots[i];
                                            const isCurrent = isCurrentHour(hour);
                                            if (cell.kind === 'empty') {
                                                return (_jsx("td", { className: "border border-gray-700/60", style: {
                                                        minWidth: 70,
                                                        backgroundColor: isCurrent ? '#EF444411' : undefined,
                                                    } }, i));
                                            }
                                            return (_jsx("td", { colSpan: cell.colSpan, className: "border border-gray-600 px-2 py-2 text-center align-middle font-bold text-white text-sm", style: {
                                                    backgroundColor: blockCellColor,
                                                    textShadow: '0 1px 3px rgba(0,0,0,0.8)',
                                                    minWidth: 70,
                                                }, children: isGameMode
                                                    ? (cell.block.gameAbbreviation ?? '—')
                                                    : (cell.block.blockName ?? 'Shift') }, i));
                                        })] }, String(key)));
                            }) })] }) }))] }));
    if (fullscreen) {
        return (_jsxs("div", { className: "fixed inset-0 z-50 bg-space-dark text-white flex flex-col", children: [_jsxs("div", { className: "flex items-center justify-between px-4 py-2 border-b border-white/10 bg-gray-900/80 backdrop-blur-sm flex-shrink-0", children: [_jsx("span", { className: "font-orbitron font-bold text-txs-red text-lg", children: "Schedule by Volunteer" }), dateSelector] }), scheduleTable] }));
    }
    return (_jsxs("div", { className: "min-h-screen bg-space-dark text-white relative", children: [_jsx(StarfieldBackground, {}), _jsx(Navigation, {}), _jsx("main", { className: "pt-20 pb-16 px-4 relative z-10", children: _jsxs("div", { className: "max-w-full mx-auto", children: [_jsxs("div", { className: "mb-6 flex items-center justify-between flex-wrap gap-4", children: [_jsx("h1", { className: "font-orbitron font-bold text-4xl text-txs-red", children: "Schedule by Volunteer" }), dateSelector] }), scheduleTable] }) })] }));
}
